import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Box, CircularProgress, Typography } from "@mui/material";
import { AdminLayout } from "../../../../components/layouts/admin-layout/admin-layout";
import { ReadyDateTableEditor } from "../../../../components/admin/edit-pickup-time-drawer/ReadyDateTableEditor";
import { ADD_STORE_CONFIG, GET_STORE_CONFIG } from "../../../../configs/apiConfig";
import { Colors } from "../../../../configs/colors";
import {
  IReadyDateRule,
  READY_DATE_MODE_DAYS,
  READY_DATE_MODE_TABLE,
  ReadyDateMode,
  withDefaultRows,
} from "../../../../configs/readyDateTable";
import { useAppSelector } from "../../../../hooks";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import { BaseResponseSingle } from "../../../../typings";
import {
  IStoreConfigResponse,
  IStoreConfigSave,
} from "../../../../typings/interfaces/responses/store-config-response";
import { primaryAlpha } from "../../../../configs/colors";

const BORDER = "#E5E7EB";
const TEXT = "#4B5563";
const MUTED = "#6B7280";

interface IServiceForm {
  enabled: boolean;
  days: number;
  /** HH:mm when the service uses its own ready time, else null */
  ownReadyTime: string | null;
}

interface IWorkflowForm {
  cutOffTime: string;
  readyTime: string;
  mode: ReadyDateMode;
  regular: IServiceForm;
  special: IServiceForm;
  rules: IReadyDateRule[];
}

const hhmm = (time?: string | null) => (time ? time.slice(0, 5) : "");

const toForm = (config: IStoreConfigResponse): IWorkflowForm => ({
  cutOffTime: hhmm(config.cutOffTime) || "10:00",
  readyTime: hhmm(config.pickupTime) || "15:00",
  mode: config.readyDateMode === READY_DATE_MODE_TABLE ? READY_DATE_MODE_TABLE : READY_DATE_MODE_DAYS,
  regular: {
    enabled: config.regularServiceEnabled !== false,
    days: config.daysToDeliver ?? 2,
    ownReadyTime: config.regularReadyTime ? hhmm(config.regularReadyTime) : null,
  },
  special: {
    enabled: config.specialServiceEnabled !== false,
    days: config.specialDaysToDeliver ?? 0,
    ownReadyTime: config.specialReadyTime ? hhmm(config.specialReadyTime) : null,
  },
  rules: withDefaultRows(config.readyDateRules),
});

const turnaroundLabel = (days: number) =>
  days === 0 ? "Same Day" : `+ ${days} Day${days === 1 ? "" : "s"}`;

// ── Controls ─────────────────────────────────────────────────────────────────

const Toggle: FC<{ checked: boolean; onChange(next: boolean): void; label: string; disabled?: boolean }> = ({
  checked,
  onChange,
  label,
  disabled,
}) => (
  <Box
    component="button"
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    sx={{
      position: "relative",
      width: "3rem",
      height: "1.6rem",
      flexShrink: 0,
      border: "none",
      borderRadius: "99px",
      p: 0,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
      backgroundColor: checked ? Colors.PRIMARY : "#D1D5DB",
      transition: "background-color 0.2s",
      "&:focus-visible": { outline: `2px solid ${Colors.PRIMARY}`, outlineOffset: "2px" },
      "&::after": {
        content: '""',
        position: "absolute",
        top: "0.25rem",
        left: checked ? "1.65rem" : "0.25rem",
        width: "1.1rem",
        height: "1.1rem",
        borderRadius: "50%",
        backgroundColor: "#ffffff",
        transition: "left 0.2s",
      },
    }}
  />
);

const fieldSx = (disabled?: boolean) => ({
  height: "2.9rem",
  borderRadius: "0.7rem",
  border: `1px solid ${BORDER}`,
  backgroundColor: disabled ? "#F3F4F6" : "#ffffff",
  color: disabled ? "#9CA3AF" : TEXT,
  fontFamily: "inherit",
  fontSize: "var(--fs-xs)",
  fontWeight: 600,
  px: "0.85rem",
  outline: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  "&:focus": { borderColor: Colors.PRIMARY, boxShadow: `0 0 0 3px ${primaryAlpha(0.10)}` },
});

const TimeInput: FC<{ value: string; onChange(v: string): void; label: string; disabled?: boolean }> = ({
  value,
  onChange,
  label,
  disabled,
}) => (
  <Box
    component="input"
    type="time"
    aria-label={label}
    value={value}
    disabled={disabled}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
    sx={{ ...fieldSx(disabled), width: "12rem", flexShrink: 0 }}
  />
);

const Select: FC<{
  value: string;
  options: { value: string; label: string }[];
  onChange(v: string): void;
  label: string;
  disabled?: boolean;
}> = ({ value, options, onChange, label, disabled }) => (
  <Box
    component="select"
    aria-label={label}
    value={value}
    disabled={disabled}
    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
    sx={{ ...fieldSx(disabled), width: "100%" }}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>
        {o.label}
      </option>
    ))}
  </Box>
);

// ── Building blocks ──────────────────────────────────────────────────────────

const Section: FC<{
  title: string;
  description: string;
  action?: ReactNode;
  dimmed?: boolean;
  testId?: string;
  children: ReactNode;
}> = ({ title, description, action, dimmed, testId, children }) => (
  <Box
    component="section"
    aria-label={title}
    data-testid={testId}
    sx={{
      borderRadius: "1.35rem",
      border: `1px solid ${BORDER}`,
      backgroundColor: "#F2F2F2",
      p: "1rem",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      opacity: dimmed ? 0.55 : 1,
      transition: "opacity 0.2s",
      minWidth: 0,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", mb: "1rem" }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="h2" sx={{ fontSize: "var(--fs-md)", fontWeight: 700, color: TEXT }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: "var(--fs-2xs)", color: MUTED, mt: "0.3rem" }}>{description}</Typography>
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

const EnablePill: FC<{ checked: boolean; onChange(next: boolean): void; label: string }> = ({
  checked,
  onChange,
  label,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "0.7rem",
      borderRadius: "99px",
      backgroundColor: "#ffffff",
      p: "0.5rem 1rem",
      boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
      flexShrink: 0,
    }}
  >
    <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 700, color: MUTED }}>Enable</Typography>
    <Toggle checked={checked} onChange={onChange} label={label} />
  </Box>
);

const card = {
  borderRadius: "1.35rem",
  border: `1px solid ${BORDER}`,
  backgroundColor: "#ffffff",
  p: "1rem 1.2rem",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  minWidth: 0,
};

const TimeCard: FC<{ title: string; description: string; label: string; value: string; onChange(v: string): void }> = ({
  title,
  description,
  label,
  value,
  onChange,
}) => (
  <Box sx={card}>
    <Typography component="h3" sx={{ fontSize: "var(--fs-base)", fontWeight: 700, color: TEXT }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: "var(--fs-2xs)", color: MUTED, mb: "0.7rem" }}>{description}</Typography>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.85rem", flexWrap: "wrap" }}>
      <Typography sx={{ fontSize: "var(--fs-xs)", fontWeight: 600, color: MUTED }}>{label}</Typography>
      <TimeInput value={value} onChange={onChange} label={title} />
    </Box>
  </Box>
);

const TURNAROUND = [0, 1, 2, 3, 4];

const ServiceCard: FC<{
  title: string;
  badge: string;
  service: IServiceForm;
  readyTime: string;
  disabled: boolean;
  onChange(next: IServiceForm): void;
}> = ({ title, badge, service, readyTime, disabled, onChange }) => {
  const off = disabled || !service.enabled;
  const own = service.ownReadyTime !== null;
  const days = TURNAROUND.includes(service.days) ? TURNAROUND : [...TURNAROUND, service.days].sort((a, b) => a - b);
  return (
    <Box sx={card} data-testid={`service-${title}`}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.85rem", mb: "1rem" }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.7rem", flexWrap: "wrap" }}>
            <Typography component="h3" sx={{ fontSize: "var(--fs-base)", fontWeight: 700, color: TEXT }}>
              {title}
            </Typography>
            <Box
              component="span"
              sx={{
                borderRadius: "99px",
                backgroundColor: "#F3F4F6",
                p: "0.15rem 0.7rem",
                fontSize: "var(--fs-2xs)",
                fontWeight: 700,
                color: MUTED,
              }}
            >
              {badge}
            </Box>
          </Box>
          <Typography sx={{ fontSize: "var(--fs-2xs)", color: MUTED, mt: "0.3rem" }}>
            Configure turnaround and pickup ready behavior.
          </Typography>
        </Box>
        <Toggle
          checked={service.enabled}
          disabled={disabled}
          onChange={(enabled) => onChange({ ...service, enabled })}
          label={`${title} on`}
        />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "0.85rem", mb: "0.85rem" }}>
        <Box>
          <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 700, color: MUTED, mb: "0.5rem" }}>
            Turnaround
          </Typography>
          <Select
            label={`${title} turnaround`}
            value={String(service.days)}
            disabled={off}
            options={days.map((d) => ({ value: String(d), label: turnaroundLabel(d) }))}
            onChange={(v) => onChange({ ...service, days: Number(v) })}
          />
        </Box>
        <Box>
          <Typography sx={{ fontSize: "var(--fs-2xs)", fontWeight: 700, color: MUTED, mb: "0.5rem" }}>
            Ready Time Source
          </Typography>
          <Select
            label={`${title} ready time source`}
            value={own ? "custom" : "store"}
            disabled={off}
            options={[
              { value: "store", label: "Use Ready Time" },
              { value: "custom", label: "Customized Ready Time" },
            ]}
            onChange={(v) => onChange({ ...service, ownReadyTime: v === "custom" ? readyTime : null })}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.85rem",
          flexWrap: "wrap",
          borderRadius: "0.85rem",
          border: "1.5px dashed #CBD5E1",
          backgroundColor: "#FAFAFA",
          p: "0.7rem 1rem",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "var(--fs-xs)", fontWeight: 700, color: TEXT }}>Ready Time</Typography>
          <Typography sx={{ fontSize: "var(--fs-2xs)", color: MUTED }}>
            {own ? "Custom time set for this service." : "Automatically uses the top Ready Time setting."}
          </Typography>
        </Box>
        <TimeInput
          label={`${title} ready time`}
          value={own ? service.ownReadyTime! : readyTime}
          disabled={off || !own}
          onChange={(v) => onChange({ ...service, ownReadyTime: v })}
        />
      </Box>
    </Box>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

export const WorkflowPage: FC = () => {
  const { showSnackbar } = useSnackbarContext();
  const { adminDashboardSelectedStore, adminDashboardSelectedMerchant } = useAppSelector((state) => state.master);
  const storeId = adminDashboardSelectedStore?.storeId;
  const merchantId = adminDashboardSelectedMerchant?.merchantId;
  const [config, setConfig] = useState<IStoreConfigResponse | null>(null);
  const [form, setForm] = useState<IWorkflowForm | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!storeId || !merchantId) return;
    setLoading(true);
    try {
      const res = await axios.get<BaseResponseSingle<IStoreConfigResponse>>(GET_STORE_CONFIG(storeId, merchantId));
      if (res.data?.status !== "SUCCESS" || !res.data.body) {
        throw new Error(res.data?.message || "Could not load the store's workflow");
      }
      setConfig(res.data.body);
      setForm(toForm(res.data.body));
    } catch (err: any) {
      setConfig(null);
      setForm(null);
      showSnackbar(err?.message || "Could not load the store's workflow", "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, merchantId]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(
    () => !!config && !!form && JSON.stringify(form) !== JSON.stringify(toForm(config)),
    [config, form]
  );

  const update = (patch: Partial<IWorkflowForm>) => setForm((f) => (f ? { ...f, ...patch } : f));

  const save = async () => {
    if (!config || !form) return;
    setSaving(true);
    const body: IStoreConfigSave = {
      ...config,
      cutOffTime: `${form.cutOffTime}:00`,
      pickupTime: `${form.readyTime}:00`,
      readyDateMode: form.mode,
      readyDateRules: form.rules,
      daysToDeliver: form.regular.days,
      specialDaysToDeliver: form.special.days,
      regularServiceEnabled: form.regular.enabled,
      specialServiceEnabled: form.special.enabled,
      regularReadyTime: form.regular.ownReadyTime ? `${form.regular.ownReadyTime}:00` : null,
      specialReadyTime: form.special.ownReadyTime ? `${form.special.ownReadyTime}:00` : null,
      clearRegularReadyTime: form.regular.ownReadyTime === null,
      clearSpecialReadyTime: form.special.ownReadyTime === null,
    };
    try {
      const res = await axios.post<BaseResponseSingle<IStoreConfigResponse>>(ADD_STORE_CONFIG, body);
      if (res.data?.status !== "SUCCESS" || !res.data.body) {
        throw new Error(res.data?.message || "Could not save the workflow");
      }
      setConfig(res.data.body);
      setForm(toForm(res.data.body));
      showSnackbar("Workflow saved", "success");
    } catch (err: any) {
      showSnackbar(err?.message || "Could not save the workflow", "error");
    } finally {
      setSaving(false);
    }
  };

  const standard = form?.mode === READY_DATE_MODE_DAYS;

  return (
    <AdminLayout>
      {!storeId || !merchantId ? (
        <Typography sx={{ fontSize: "var(--fs-base)", color: MUTED }}>
          Select a store in the header to set up its workflow.
        </Typography>
      ) : loading || !form ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: "3rem" }}>
          {loading ? <CircularProgress /> : null}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {/* Save bar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1rem",
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: "var(--fs-xs)", color: MUTED }}>
              Ready dates for <strong>{adminDashboardSelectedStore?.storeName}</strong>
              {dirty ? " · Unsaved changes" : ""}
            </Typography>
            <Box sx={{ display: "flex", gap: "0.7rem" }}>
              <Box
                component="button"
                type="button"
                disabled={!dirty || saving}
                onClick={() => config && setForm(toForm(config))}
                sx={{
                  height: "3.2rem",
                  px: "1.4rem",
                  borderRadius: "0.85rem",
                  border: `1px solid ${BORDER}`,
                  backgroundColor: "#ffffff",
                  color: TEXT,
                  fontFamily: "inherit",
                  fontSize: "var(--fs-xs)",
                  fontWeight: 600,
                  cursor: !dirty || saving ? "not-allowed" : "pointer",
                  opacity: !dirty || saving ? 0.5 : 1,
                }}
              >
                Discard
              </Box>
              <Box
                component="button"
                type="button"
                disabled={!dirty || saving}
                onClick={save}
                sx={{
                  height: "3.2rem",
                  minWidth: "11rem",
                  px: "1.4rem",
                  borderRadius: "0.85rem",
                  border: "none",
                  backgroundColor: !dirty || saving ? "#D1D5DB" : Colors.PRIMARY,
                  color: "#ffffff",
                  fontFamily: "inherit",
                  fontSize: "var(--fs-xs)",
                  fontWeight: 600,
                  cursor: !dirty || saving ? "not-allowed" : "pointer",
                  "&:hover": { backgroundColor: !dirty || saving ? "#D1D5DB" : Colors.PRIMARY_DARK },
                }}
              >
                {saving ? "Saving…" : "Save Changes"}
              </Box>
            </Box>
          </Box>

          <Section
            title="Common Time Settings"
            description="Used by both Regular Service and Special Service unless customized below."
          >
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: "1rem" }}>
              <TimeCard
                title="Same Day Service Cutoff Time"
                description="Last drop-off time for same day service."
                label="Orders must be dropped off by"
                value={form.cutOffTime}
                onChange={(cutOffTime) => update({ cutOffTime })}
              />
              <TimeCard
                title="Ready Time"
                description="Default pickup ready time for enabled services."
                label="Orders ready for pickup by"
                value={form.readyTime}
                onChange={(readyTime) => update({ readyTime })}
              />
            </Box>
          </Section>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "0.9fr 1.1fr" }, gap: "1rem" }}>
            <Section
              title="Standard Drop-off Schedule"
              description="Set turnaround by service. Ready time comes from the top setting unless changed here."
              dimmed={!standard}
              testId="standard-schedule"
              action={
                <EnablePill
                  label="Standard schedule"
                  checked={standard}
                  onChange={(on) => update({ mode: on ? READY_DATE_MODE_DAYS : READY_DATE_MODE_TABLE })}
                />
              }
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: "0.7rem" }}>
                <ServiceCard
                  title="Regular Service"
                  badge="Default"
                  service={form.regular}
                  readyTime={form.readyTime}
                  disabled={!standard}
                  onChange={(regular) => update({ regular })}
                />
                <ServiceCard
                  title="Special Service"
                  badge="Priority"
                  service={form.special}
                  readyTime={form.readyTime}
                  disabled={!standard}
                  onChange={(special) => update({ special })}
                />
              </Box>
            </Section>

            <Section
              title="Customized Schedule"
              description="Controls service ready date by drop-off day and cutoff status."
              dimmed={standard}
              testId="customized-schedule"
              action={
                <EnablePill
                  label="Customized schedule"
                  checked={!standard}
                  onChange={(on) => update({ mode: on ? READY_DATE_MODE_TABLE : READY_DATE_MODE_DAYS })}
                />
              }
            >
              <Box sx={{ overflowX: "auto", borderRadius: "0.85rem", backgroundColor: "#ffffff" }}>
                <ReadyDateTableEditor rules={form.rules} disabled={standard} onChange={(rules) => update({ rules })} />
              </Box>
            </Section>
          </Box>
        </Box>
      )}
    </AdminLayout>
  );
};
